"use client";

/**
 * Cube — featured posts as a pure-CSS 3D prism. Progressive enhancement over
 * a flat semantic list; the flourish of the blog's identity layer.
 *
 * GEOMETRY (the "cleanest geometry for <= 6" decision): a regular N-gonal
 * PRISM where N = faces.length clamped to [2, 6]. Face i sits at
 * `rotateY(i * 360/N) translateZ(r)` with r = apothem of the regular N-gon:
 *
 *     r = faceWidth / (2 * tan(PI / N))
 *
 * N=4 is the classic cube; 3/5/6 are shallower/deeper prisms from the same
 * formula — one code path, no special cases. N=2 degenerates (tan(PI/2) is
 * infinite) to a two-sided card at r = 0.5px so the faces don't z-fight.
 * A single post renders the flat list only — no 3D for one face.
 *
 * LAYERS: viewport (perspective) > depth (translateZ(-r), pulls the assembly
 * back so the front face sits at the screen plane) > spinner (the CSS-keyframe
 * idle rotation — ~one face per 5s, i.e. 360deg over N*5s) > stage (the
 * JS/manual rotation: drag, keyboard, focus — animated with motion's spring)
 * > faces. Y-rotations commute, so idle and manual angles simply add.
 *
 * PROGRESSIVE ENHANCEMENT: the server render (and no-JS, and browsers without
 * preserve-3d) is a legible flat grid of the same links. The cube mounts only
 * after hydration when CSS.supports confirms preserve-3d. For n >= 2 (the
 * swap-capable case) BOTH states render inside the identical footprint — a
 * controls-row ghost plus the square stage — so the enhancement swap causes
 * zero layout shift (CLS 0 on mount). Faces carry generative covers +
 * D-numerals only, never text (spec §4.5 — text in a transformed 3D context
 * rasterizes blurry); titles live in aria-labels, on the flat fallback
 * cards, and in the semantic PostList below.
 *
 * MOTION RULES: transform/opacity only; idle spin pauses on hover, on
 * :focus-within, while dragging, and via the visible pause button (WCAG
 * 2.2.2, first in DOM order). prefers-reduced-motion: no idle spin, the
 * pause button is not rendered (nothing auto-moves), and prev/next step
 * instantly. Nothing here is scroll-linked.
 *
 * A11Y: region with aria-roledescription="carousel"; non-front faces are
 * inert + aria-hidden (one tab stop — the front link); ArrowLeft/ArrowRight
 * rotate; focus landing on a face rotates it to front.
 *
 * KEEP THIS A LAZY LEAF: only React, next/link, motion/mini + the spring
 * generator, and DNumeral. Consumers should import it with next/dynamic so
 * article routes never ship cube JS.
 */

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { animate } from "motion/mini";
import { spring } from "motion";
import type { TrackId } from "@/lib/blog/anchor";
import { DNumeral } from "./DNumeral";
import styles from "./cube.module.css";

export type CubeFace = {
  href: string;
  title: string;
  track: TrackId;
  /** 1-based week of the arc this entry covers, when it has one. */
  week?: number;
  coverSvg: string;
};

export type CubeProps = {
  faces: CubeFace[];
  className?: string;
};

const MAX_FACES = 6;
const DRAG_DEG_PER_PX = 0.35;
const DRAG_THRESHOLD_PX = 5;
const SECONDS_PER_FACE = 5;

/** Normalize an angle difference to (-180, 180]. */
function shortestDelta(deg: number): number {
  return ((deg % 360) + 540) % 360 - 180;
}

/** Read the current rotateY of an element (deg, wrapped to (-180, 180]).
 * Valid because we only ever put pure Y-rotations on these elements. */
function readYAngle(el: HTMLElement): number {
  const t = getComputedStyle(el).transform;
  if (!t || t === "none") return 0;
  const m = new DOMMatrixReadOnly(t);
  // rotateY(a): m11 = cos(a), m31 = sin(a)
  return (Math.atan2(m.m31, m.m11) * 180) / Math.PI;
}

/** Unwrap a wrapped angle to the full-turn branch nearest `near`. */
function unwrapNear(wrapped: number, near: number): number {
  return near + shortestDelta(wrapped - near);
}

/* Hydration-safe external stores (server snapshots return false, so the
   server and hydration renders are the flat list; the cube mounts after). */
const subscribeNever = () => () => {};

function getSupports3d(): boolean {
  return (
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("transform-style", "preserve-3d") &&
    CSS.supports("perspective", "1px")
  );
}

const REDUCED_MQ = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void): () => void {
  const mq = window.matchMedia(REDUCED_MQ);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getReducedMotion(): boolean {
  return window.matchMedia(REDUCED_MQ).matches;
}

const getFalse = () => false;

type DragState = {
  pointerId: number;
  startX: number;
  baseManual: number;
  captured: boolean;
};

export function Cube({ faces, className }: CubeProps) {
  const shown = faces.slice(0, MAX_FACES);
  const n = shown.length;

  // hydration-safe: false on the server + hydration render, real value after
  const supports3d = useSyncExternalStore(subscribeNever, getSupports3d, getFalse);
  const reduced = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    getFalse,
  );
  const enhanced = supports3d && n >= 2;

  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [front, setFront] = useState(0);
  const [stageW, setStageW] = useState(0);

  const viewportRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const faceLinkRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  /** Current TARGET of the manual rotation, degrees (unwrapped). */
  const manualRef = useRef(0);
  const animRef = useRef<ReturnType<typeof animate> | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const pendingFocusRef = useRef<number | null>(null);

  // ── measure the viewport so faceW/radius track the layout ────────────────
  // (ResizeObserver delivers an initial observation on observe(), so no
  //  synchronous first measure is needed.)
  useEffect(() => {
    if (!enhanced) return;
    const vp = viewportRef.current;
    if (!vp) return;
    const ro = new ResizeObserver(() => setStageW(vp.clientWidth));
    ro.observe(vp);
    return () => ro.disconnect();
  }, [enhanced]);

  const computeFront = useCallback(() => {
    const spinner = spinnerRef.current;
    if (!spinner || n < 2) return 0;
    const total = readYAngle(spinner) + manualRef.current;
    const step = 360 / n;
    return ((Math.round(-total / step) % n) + n) % n;
  }, [n]);

  // ── track which face is front while the idle spin drifts (a11y state) ────
  useEffect(() => {
    if (!enhanced || reduced) return;
    const id = window.setInterval(() => {
      if (dragRef.current?.captured) return;
      const next = computeFront();
      setFront((f) => (f === next ? f : next));
    }, 600);
    return () => window.clearInterval(id);
  }, [enhanced, reduced, computeFront]);

  // ── DEV-only Safari trap audit: preserve-3d silently flattens when an
  //    ancestor carries overflow/filter/mask/clip-path (or opacity < 1) ─────
  useEffect(() => {
    if (!enhanced || process.env.NODE_ENV === "production") return;
    const vp = viewportRef.current;
    if (!vp) return;
    const offenders: string[] = [];
    let el: HTMLElement | null = vp.parentElement;
    while (el && el !== document.body) {
      const cs = getComputedStyle(el);
      const bad: string[] = [];
      if (cs.overflowX !== "visible") bad.push(`overflow-x:${cs.overflowX}`);
      if (cs.overflowY !== "visible") bad.push(`overflow-y:${cs.overflowY}`);
      if (cs.filter !== "none") bad.push(`filter:${cs.filter}`);
      if (cs.clipPath !== "none") bad.push(`clip-path:${cs.clipPath}`);
      const mask = cs.maskImage || "none";
      if (mask !== "none") bad.push(`mask-image:${mask}`);
      if (parseFloat(cs.opacity) < 1) bad.push(`opacity:${cs.opacity}`);
      if (bad.length > 0) {
        const cls = el.getAttribute("class") ?? "";
        offenders.push(
          `  <${el.tagName.toLowerCase()}${cls ? ` class="${cls}"` : ""}> — ${bad.join(", ")}`,
        );
      }
      el = el.parentElement;
    }
    if (offenders.length > 0) {
      console.warn(
        "[Cube] preserve-3d hazard: ancestor(s) carry styles that flatten or clip 3D (Safari especially). Fix the ancestor chain:\n" +
          offenders.join("\n"),
      );
    }
  }, [enhanced]);

  // ── rotation engine ──────────────────────────────────────────────────────
  const rotateToFront = useCallback(
    (idx: number) => {
      const stage = stageRef.current;
      const spinner = spinnerRef.current;
      if (!stage || !spinner || n < 2) return;
      const step = 360 / n;
      const idle = readYAngle(spinner);
      const currentTotal = idle + manualRef.current;
      const delta = shortestDelta(-idx * step - currentTotal);
      setFront(((idx % n) + n) % n);
      if (Math.abs(delta) < 0.5) return;
      const target = manualRef.current + delta;
      manualRef.current = target;
      animRef.current?.stop();
      if (reduced) {
        stage.style.transform = `rotateY(${target}deg)`;
      } else {
        animRef.current = animate(
          stage,
          { transform: `rotateY(${target}deg)` },
          { type: spring, stiffness: 170, damping: 24 },
        );
      }
    },
    [n, reduced],
  );

  const stepBy = useCallback(
    (dir: 1 | -1) => {
      const target = ((front + dir) % n + n) % n;
      const stage = stageRef.current;
      if (stage && stage.contains(document.activeElement)) {
        pendingFocusRef.current = target;
      }
      rotateToFront(target);
    },
    [front, n, rotateToFront],
  );

  // after a keyboard step, move focus onto the (now non-inert) front link
  useEffect(() => {
    if (pendingFocusRef.current === front) {
      pendingFocusRef.current = null;
      faceLinkRefs.current[front]?.focus({ preventScroll: true });
    }
  }, [front]);

  useEffect(() => () => animRef.current?.stop(), []);

  // ── pointer drag (rotation follows the pointer; spring-snap on release) ──
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    suppressClickRef.current = false;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      baseManual: manualRef.current,
      captured: false,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    const stage = stageRef.current;
    if (!d || !stage || e.pointerId !== d.pointerId) return;
    if (!d.captured) {
      // don't steal plain taps/clicks from the face links
      if (Math.abs(e.clientX - d.startX) < DRAG_THRESHOLD_PX) return;
      d.captured = true;
      viewportRef.current?.setPointerCapture(e.pointerId);
      setDragging(true);
      animRef.current?.stop();
      // re-baseline from the actual on-screen angle (a spring may be mid-flight)
      d.baseManual = unwrapNear(readYAngle(stage), manualRef.current);
      manualRef.current = d.baseManual;
      d.startX = e.clientX;
      return;
    }
    const deg = d.baseManual + (e.clientX - d.startX) * DRAG_DEG_PER_PX;
    manualRef.current = deg;
    stage.style.transform = `rotateY(${deg}deg)`;
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    if (!d.captured) return;
    try {
      viewportRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* capture already released */
    }
    setDragging(false);
    suppressClickRef.current = true;
    // snap the TOTAL angle to the nearest face
    const stage = stageRef.current;
    const spinner = spinnerRef.current;
    if (!stage || !spinner || n < 2) return;
    const step = 360 / n;
    const idle = readYAngle(spinner);
    const snapped = Math.round(-(idle + manualRef.current) / step);
    const target = -snapped * step - idle;
    manualRef.current = target;
    animRef.current?.stop();
    if (reduced) {
      stage.style.transform = `rotateY(${target}deg)`;
    } else {
      animRef.current = animate(
        stage,
        { transform: `rotateY(${target}deg)` },
        { type: spring, stiffness: 170, damping: 24 },
      );
    }
    setFront(((snapped % n) + n) % n);
  };

  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      stepBy(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      stepBy(-1);
    }
  };

  if (n === 0) return null;

  const rootClass = className ? `${styles.root} ${className}` : styles.root;

  // ── layer 0: the legible flat list (SSR, no-JS, no preserve-3d, n === 1) ─
  if (!enhanced || n < 2) {
    const cards = shown.map((f) => (
      <li key={f.href}>
        <Link href={f.href} className={styles.card} draggable={false}>
          <span
            aria-hidden="true"
            className={styles.cover}
            dangerouslySetInnerHTML={{ __html: f.coverSvg }}
          />
          <span aria-hidden="true" className={styles.scrim} />
          <span className={styles.cardTrack}>
            {f.track}
            {f.week != null ? ` · week ${f.week}` : ""}
          </span>
          {f.week != null ? (
            <DNumeral week={f.week} className={styles.cardNumeral} />
          ) : null}
          <span className={styles.cardTitle}>{f.title}</span>
        </Link>
      </li>
    ));

    // n === 1 never enhances into the cube, so it keeps the free-flowing grid.
    if (n < 2) {
      return (
        <div className={rootClass}>
          <ul className={styles.grid}>{cards}</ul>
        </div>
      );
    }

    // Swap-capable: reserve the cube's exact footprint (controls ghost +
    // square stage) so the post-hydration enhancement shifts nothing.
    return (
      <div className={rootClass}>
        <div className={styles.controlsGhost} aria-hidden="true" />
        <div className={styles.squareStage}>
          <ul
            className={styles.gridSquare}
            data-cols={n >= 4 ? "2" : undefined}
          >
            {cards}
          </ul>
        </div>
      </div>
    );
  }

  // ── the prism ────────────────────────────────────────────────────────────
  const step = 360 / n;
  const faceW = Math.round(stageW * (n <= 4 ? 0.86 : 0.74));
  const radius =
    n === 2 ? 0.5 : Math.max(0.5, faceW / (2 * Math.tan(Math.PI / n)));

  return (
    <section
      className={rootClass}
      aria-roledescription="carousel"
      aria-label="Featured posts"
      data-paused={paused || undefined}
      data-dragging={dragging || undefined}
      onKeyDown={onKeyDown}
    >
      {/* controls first in DOM order — pause before everything (WCAG 2.2.2) */}
      <div className={styles.controls}>
        {!reduced ? (
          <button
            type="button"
            className={styles.ctrlBtn}
            aria-pressed={paused}
            aria-label={paused ? "Resume rotation" : "Pause rotation"}
            onClick={() => setPaused((p) => !p)}
          >
            {paused ? (
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12">
                <path d="M2.5 1.5l8 4.5-8 4.5z" fill="currentColor" />
              </svg>
            ) : (
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12">
                <path d="M2.5 1.5h2.4v9H2.5zM7.1 1.5h2.4v9H7.1z" fill="currentColor" />
              </svg>
            )}
          </button>
        ) : null}
        <button
          type="button"
          className={styles.ctrlBtn}
          aria-label="Previous post"
          onClick={() => stepBy(-1)}
        >
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M8 1.5L3.5 6 8 10.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className={styles.ctrlBtn}
          aria-label="Next post"
          onClick={() => stepBy(1)}
        >
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M4 1.5L8.5 6 4 10.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div
        ref={viewportRef}
        className={styles.viewport}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        <div
          className={styles.depth}
          style={{ transform: `translateZ(${-radius}px)` }}
        >
          <div
            ref={spinnerRef}
            className={styles.spinner}
            style={{ animationDuration: `${n * SECONDS_PER_FACE}s` }}
          >
            <div ref={stageRef} className={styles.stage}>
              {shown.map((f, i) => {
                const isFront = i === front;
                return (
                  <div
                    key={f.href}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${i + 1} of ${n}`}
                    aria-hidden={!isFront}
                    inert={!isFront}
                    className={styles.face}
                    data-front={isFront || undefined}
                    style={{
                      width: faceW,
                      height: faceW,
                      transform: `translate(-50%, -50%) rotateY(${
                        i * step
                      }deg) translateZ(${radius}px)`,
                    }}
                  >
                    <Link
                      href={f.href}
                      className={styles.faceLink}
                      draggable={false}
                      aria-label={
                        f.week != null ? `${f.title} — week ${f.week}` : f.title
                      }
                      ref={(el) => {
                        faceLinkRefs.current[i] = el;
                      }}
                      onFocus={() => rotateToFront(i)}
                    >
                      <span
                        aria-hidden="true"
                        className={styles.cover}
                        dangerouslySetInnerHTML={{ __html: f.coverSvg }}
                      />
                      <span aria-hidden="true" className={styles.scrim} />
                      {/* covers + numerals only on faces — the title is in
                          the aria-label and the PostList below (spec §4.5) */}
                      {f.week != null ? (
                        <DNumeral week={f.week} className={styles.faceNumeral} />
                      ) : null}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Cube;
